/** Contabilidad → Sumas y saldos del ejercicio, con acumulados por grupo, subgrupo y cuenta de tres dígitos. */
import { useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { usePgcIssuer, useCanSeeLedger } from '../../lib/ctb'
import CtbHeader from '../../components/ctb/CtbHeader'
import { apiMessage } from '../../components/ctb/EntryModal'
import { Note, selectCls, td, tdR, th, thR } from '../../components/accounting/common'
import type { CtbTrialBalanceResponse } from '@toga/shared'

const LEVELS: Record<string, { label: string; levels: string }> = {
  groups: { label: 'Grupos', levels: '1' },
  subgroups: { label: 'Grupos y subgrupos', levels: '1,2' },
  threeDigits: { label: 'Hasta cuentas de 3 dígitos', levels: '1,2,3' },
  detail: { label: 'Todo el detalle', levels: '1,2,3' },
}

export default function TrialBalancePage() {
  const canSee = useCanSeeLedger()
  const { pgc, issuerId, setIssuerId, loaded } = usePgcIssuer()
  const [year, setYear] = useState(new Date().getFullYear())
  const [view, setView] = useState('threeDigits')
  const [excludeClosing, setExcludeClosing] = useState(false)
  const [data, setData] = useState<CtbTrialBalanceResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setData(null)
    setError(null)
    if (!issuerId) return
    let alive = true
    const p = new URLSearchParams({ issuerId, year: String(year), levels: LEVELS[view]!.levels })
    if (excludeClosing) p.set('excludeClosing', '1')
    api
      .get<CtbTrialBalanceResponse>(`/ctb/trial-balance?${p}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(apiMessage(err, 'No se pudo cargar')))
    return () => {
      alive = false
    }
  }, [issuerId, year, view, excludeClosing])

  if (!canSee)
    return (
      <p className="t-muted text-sm">
        Las sumas y saldos son solo para administradores y abogados.
      </p>
    )

  const rows = (data?.rows ?? []).filter((r) => {
    if (view === 'detail') return r.rollUpLevel === null || r.rollUpLevel <= 2
    if (view === 'threeDigits') return r.rollUpLevel !== null
    return true
  })

  return (
    <div className="space-y-5 animate-slideUp">
      <CtbHeader
        title="Contabilidad · Sumas y saldos"
        description="Debe y haber acumulados por cuenta y por grupo, con su saldo deudor o acreedor. Si el debe y el haber totales no coinciden, algo está mal."
        pgc={pgc}
        loaded={loaded}
        issuerId={issuerId}
        onIssuer={setIssuerId}
        year={year}
        onYear={setYear}
      >
        <select
          value={view}
          onChange={(e) => setView(e.target.value)}
          className={selectCls}
          aria-label="Nivel de detalle"
        >
          {Object.entries(LEVELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <label className="text-xs t-muted inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={excludeClosing}
            onChange={(e) => setExcludeClosing(e.target.checked)}
            className="accent-toga-500"
          />
          Sin asientos de cierre
        </label>
        {data && (
          <span
            className={[
              'ml-auto text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1',
              data.totals.balanced
                ? 'text-emerald-300 border-emerald-500/40'
                : 'text-rose-300 border-rose-500/40',
            ].join(' ')}
          >
            {data.totals.balanced ? (
              <>
                <CheckCircle2 size={12} /> Cuadra
              </>
            ) : (
              <>
                <AlertCircle size={12} /> No cuadra
              </>
            )}
          </span>
        )}
      </CtbHeader>

      {error && (
        <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {issuerId && (
        <section className="surface overflow-hidden">
          {!data ? (
            <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Cargando…
            </div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-10 text-center t-muted text-sm">
              No hay asientos contabilizados en {year}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-base">
                    <th className={th}>Cuenta</th>
                    <th className={th}>Nombre</th>
                    <th className={thR}>Suma debe</th>
                    <th className={thR}>Suma haber</th>
                    <th className={thR}>Saldo deudor</th>
                    <th className={thR}>Saldo acreedor</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const group = r.rollUpLevel !== null
                    return (
                      <tr
                        key={`${r.code}-${r.rollUpLevel}`}
                        className={[
                          'border-b border-base/50',
                          group ? 'bg-black/10 font-semibold' : '',
                        ].join(' ')}
                      >
                        <td className={`${td} font-mono ${group ? '' : 'pl-6 font-normal'}`}>
                          {r.code}
                        </td>
                        <td className={`${td} ${group ? '' : 'font-normal t-secondary'}`}>
                          {r.name}
                        </td>
                        <td className={tdR}>{formatCents(r.debitCents)}</td>
                        <td className={tdR}>{formatCents(r.creditCents)}</td>
                        <td className={tdR}>
                          {r.debitBalanceCents ? formatCents(r.debitBalanceCents) : ''}
                        </td>
                        <td className={tdR}>
                          {r.creditBalanceCents ? formatCents(r.creditBalanceCents) : ''}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-base font-semibold">
                    <td className={td} colSpan={2}>
                      Total del ejercicio
                    </td>
                    <td className={tdR}>{formatCents(data.totals.debitCents)}</td>
                    <td className={tdR}>{formatCents(data.totals.creditCents)}</td>
                    <td className={td} colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <Note>
            Los acumulados (negrita) suman las cuentas de su grupo, subgrupo o código de tres
            dígitos; los totales cuentan solo las cuentas con movimiento, para no duplicar. Un
            borrador no entra.
          </Note>
        </section>
      )}
    </div>
  )
}
